import { CREDIT_COPY, type CreditSummary, textsLeft } from '@eventping/shared'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { MessageSquareOff, MessageSquareText, MessageSquareWarning } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

/** The account's text credit (docs/PRICING.md §2.8): refetched on every page load and focus. */
export function useCredit(enabled = true) {
  return useQuery({
    queryKey: ['credit'],
    queryFn: () => api<CreditSummary>('/api/account/credit'),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
  })
}

const BADGE_TONE = {
  plenty: 'border-border bg-card text-foreground hover:bg-accent',
  low: 'border-waiting/40 bg-waiting-soft text-waiting hover:bg-waiting-soft/80',
  zero: 'border-destructive/50 bg-destructive/10 text-destructive hover:bg-destructive/15',
} as const

/**
 * The header's always-visible balance: "142 texts left", "1 text left", a warning color when
 * low, and "Texts paused, email continues" at zero. Links to the text log.
 */
export function CreditBadge() {
  const credit = useCredit()
  if (!credit.data) return null
  const { balance, state } = credit.data
  const Icon =
    state === 'zero' ? MessageSquareOff : state === 'low' ? MessageSquareWarning : MessageSquareText
  const label = state === 'zero' ? CREDIT_COPY.zeroBadge : textsLeft(balance)
  const tip = state === 'zero' ? CREDIT_COPY.zero : state === 'low' ? CREDIT_COPY.low : undefined
  return (
    <Link
      to="/account/texts"
      data-testid="credit-badge"
      data-state={state}
      title={tip}
      aria-label={`${label}.${tip ? ` ${tip}` : ''} Open your text log.`}
      className={cn(
        'inline-flex min-h-9 max-w-[8.5rem] items-center gap-1.5 rounded-full border px-3 py-1 text-xs leading-tight font-medium transition-colors min-[420px]:max-w-none sm:text-sm',
        BADGE_TONE[state],
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span>{label}</span>
    </Link>
  )
}

/** On the organizer's main pages when texts are low or used up: the same states as the badge. */
export function CreditBanner() {
  const credit = useCredit()
  const data = credit.data
  if (!data || data.state === 'plenty') return null
  const zero = data.state === 'zero'
  return (
    <div
      role="status"
      data-testid="credit-banner"
      data-state={data.state}
      className={cn(
        'mb-5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border p-4 text-sm',
        zero
          ? 'border-destructive/40 bg-destructive/10 text-foreground'
          : 'border-waiting/40 bg-waiting-soft text-foreground',
      )}
    >
      {zero ? (
        <MessageSquareOff className="size-4 shrink-0 text-destructive" aria-hidden />
      ) : (
        <MessageSquareWarning className="size-4 shrink-0 text-waiting" aria-hidden />
      )}
      <p className="min-w-0 flex-1">
        {zero ? CREDIT_COPY.zeroBanner : CREDIT_COPY.lowBanner(data.balance)}
      </p>
      <Link to="/pricing" className="font-medium underline underline-offset-4">
        Buy texts
      </Link>
    </div>
  )
}
